/**
 * Change Requests — system_admin's queue of Iqra-drafted feedback.
 *
 * Lists every change request submitted via the Iqra widget. Admin can:
 *   • Filter by status / classification
 *   • Open detail (screenshot preview + admin notes)
 *   • Update status (triaged → accepted → in_progress → done | rejected)
 *
 * The Iqra widget on this very page produces the data shown here — meta and
 * delightful. Strictly admin-only (route-guarded in App.js and server-side).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  MessageSquare, Filter, X, Loader2, RefreshCcw, AlertCircle, Image as ImageIcon,
  CheckCircle2, Clock, PauseCircle, ArrowRight,
} from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const STATUS_OPTIONS = [
  { id: 'new',         label: 'New',         tone: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-200' },
  { id: 'triaged',     label: 'Triaged',     tone: 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-200' },
  { id: 'accepted',    label: 'Accepted',    tone: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200' },
  { id: 'in_progress', label: 'In Progress', tone: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200' },
  { id: 'done',        label: 'Done',        tone: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200' },
  { id: 'rejected',    label: 'Rejected',    tone: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-200' },
];

const SEV = {
  P0: 'bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200',
  P1: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  P2: 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
  P3: 'bg-slate-100 text-slate-700 dark:bg-white/10 dark:text-white/70',
};

/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the fetch + drawer effects. */
export default function ChangeRequests() {
  const [list, setList] = useState({ items: [], by_status: {}, by_severity: {}, count: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [classFilter, setClassFilter] = useState('all');
  const [active, setActive] = useState(null);   // detail row (with screenshot)
  const [activeBusy, setActiveBusy] = useState(false);

  const load = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (classFilter !== 'all')  params.classification = classFilter;
      const { data } = await api.get('/iqra/change-requests', { params });
      setList(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load change requests.');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter, classFilter]);

  const open = async (cr_id) => {
    setActive({ change_request_id: cr_id }); // open shell while fetching
    setActiveBusy(true);
    try {
      const { data } = await api.get(`/iqra/change-requests/${cr_id}`);
      setActive(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not open.');
      setActive(null);
    } finally { setActiveBusy(false); }
  };

  const updateStatus = async (newStatus) => {
    if (!active?.change_request_id) return;
    try {
      const { data } = await api.patch(`/iqra/change-requests/${active.change_request_id}`, { status: newStatus });
      setActive(data);
      await load();
      toast.success(`Marked ${newStatus.replace('_', ' ')}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update.');
    }
  };

  const saveNotes = async (notes) => {
    if (!active?.change_request_id) return;
    try {
      const { data } = await api.patch(`/iqra/change-requests/${active.change_request_id}`, { admin_notes: notes });
      setActive(data);
      await load();
      toast.success('Notes saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save notes.');
    }
  };

  const classifications = useMemo(() => {
    const set = new Set(list.items.map((c) => c.classification).filter(Boolean));
    return ['all', ...Array.from(set)];
  }, [list]);

  return (
    <div className="ve-section" data-testid="change-requests-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="ve-eyebrow flex items-center gap-2">
            <MessageSquare size={14} className="text-[#8B3FC5]"/>
            Iqra · Change Requests
          </div>
          <h1 className="display text-3xl md:text-4xl mt-2 font-bold">
            Voice of users. <span className="ve-gold-text">Drafted by Iqra.</span>
          </h1>
          <p className="text-sm text-slate-600 dark:text-white/70 mt-2 max-w-2xl leading-relaxed">
            Every feedback submission flows here. Iqra has already classified each one and drafted acceptance criteria — your job is to triage, accept and ship.
          </p>
        </div>
        <button
          data-testid="cr-refresh"
          onClick={load}
          className="ve-btn-secondary inline-flex items-center gap-2"
        >
          <RefreshCcw size={14}/> Refresh
        </button>
      </div>

      {/* Status counts strip */}
      <div className="mt-6 flex flex-wrap gap-2">
        <FilterPill
          label="All"
          n={list.count}
          active={statusFilter === 'all'}
          onClick={() => setStatusFilter('all')}
          testid="cr-filter-all"
        />
        {STATUS_OPTIONS.map((s) => (
          <FilterPill
            key={s.id}
            label={s.label}
            n={list.by_status?.[s.id] || 0}
            active={statusFilter === s.id}
            onClick={() => setStatusFilter(s.id)}
            testid={`cr-filter-${s.id}`}
            tone={s.tone}
          />
        ))}
      </div>

      {/* Classification chips */}
      {classifications.length > 2 && (
        <div className="mt-3 flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 dark:text-white/55">
          <Filter size={11}/>
          {classifications.map((c) => (
            <button
              key={c}
              data-testid={`cr-class-${c}`}
              onClick={() => setClassFilter(c)}
              className={`px-2.5 py-1 rounded-full border font-bold transition ${
                classFilter === c
                  ? 'border-[#8B3FC5] bg-[#FFF8E5] text-[#7A5A00] dark:bg-[#3a2f0a] dark:text-[#F5E1A4]'
                  : 'border-slate-200 dark:border-white/10 text-slate-600 dark:text-white/60 hover:border-[#8B3FC5]'
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      {/* List */}
      <div className="mt-6">
        {loading && (
          <div className="text-sm text-slate-500 flex items-center gap-2">
            <Loader2 size={14} className="animate-spin"/> Loading…
          </div>
        )}
        {!loading && list.items.length === 0 && (
          <div className="ve-card p-8 text-center">
            <div className="text-sm font-bold text-slate-700 dark:text-white/85">No change requests yet.</div>
            <div className="text-xs text-slate-500 dark:text-white/55 mt-1">
              Try submitting feedback from the floating Iqra widget — you&rsquo;ll see it land here.
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {list.items.map((c) => (
            <CRCard key={c.change_request_id} c={c} onOpen={() => open(c.change_request_id)} />
          ))}
        </div>
      </div>

      {/* Detail drawer */}
      {active && (
        <Drawer
          cr={active}
          busy={activeBusy}
          onClose={() => setActive(null)}
          onUpdateStatus={updateStatus}
          onSaveNotes={saveNotes}
        />
      )}
    </div>
  );
}

function CRCard({ c, onOpen }) {
  const statusMeta = STATUS_OPTIONS.find((s) => s.id === c.status) || STATUS_OPTIONS[0];
  return (
    <button
      data-testid={`cr-card-${c.change_request_id}`}
      onClick={onOpen}
      className="ve-card p-4 text-left hover:border-[#8B3FC5] hover:shadow-[0_6px_28px_-12px_rgba(174,82,234,0.5)] transition-all"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className={`text-[9px] font-bold px-2 py-0.5 rounded uppercase tracking-[0.18em] ${SEV[c.severity] || SEV.P2}`}>{c.severity || 'P2'}</span>
          <span className="text-[9px] font-bold px-2 py-0.5 rounded uppercase tracking-[0.18em] bg-slate-100 text-slate-700 dark:bg-white/10 dark:text-white/70">{c.classification}</span>
          <span className={`text-[9px] font-bold px-2 py-0.5 rounded uppercase tracking-[0.18em] ${statusMeta.tone}`}>{statusMeta.label}</span>
        </div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 shrink-0 mono">
          {new Date(c.created_at).toLocaleDateString()}
        </div>
      </div>
      <div className="mt-2 text-sm font-bold leading-snug">{c.title}</div>
      <div className="mt-1 text-xs text-slate-600 dark:text-white/70 leading-snug line-clamp-2">{c.summary}</div>
      <div className="mt-3 flex items-center justify-between gap-2 text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-white/55">
        <span className="truncate">{c.submitted_by_email}</span>
        <span className="mono text-slate-400">{c.page_url || '—'}</span>
      </div>
    </button>
  );
}

function FilterPill({ label, n, active, onClick, testid, tone }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-3 h-9 rounded-md border text-[11px] uppercase tracking-[0.12em] font-bold whitespace-nowrap transition
        ${active
          ? 'border-[#0A0A0A] bg-[#0A0A0A] text-[#8B3FC5]'
          : 'border-slate-200 dark:border-white/10 text-slate-700 dark:text-white/75 hover:border-[#8B3FC5]'}`}
    >
      <span>{label}</span>
      <span className={`mono text-[10px] px-1.5 rounded ${tone || 'bg-slate-100 text-slate-700 dark:bg-white/10 dark:text-white/70'}`}>{n}</span>
    </button>
  );
}

function Drawer({ cr, busy, onClose, onUpdateStatus, onSaveNotes }) {
  const [notes, setNotes] = useState(cr.admin_notes || '');
  useEffect(() => { setNotes(cr.admin_notes || ''); }, [cr.change_request_id, cr.admin_notes]);

  return (
    <div
      data-testid="cr-drawer"
      className="fixed inset-0 z-40 flex items-stretch justify-end bg-black/50"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl bg-white dark:bg-[#0d0d0d] shadow-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drawer header */}
        <div className="sticky top-0 z-10 bg-white dark:bg-[#0d0d0d] border-b border-slate-200 dark:border-white/10 px-5 py-4 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#5E1F8B] dark:text-[#8B3FC5] font-bold">Change Request</div>
            <div className="mono text-xs text-slate-500 dark:text-white/55 mt-0.5">{cr.change_request_id}</div>
          </div>
          <button
            data-testid="cr-drawer-close"
            onClick={onClose}
            className="p-2 hover:bg-slate-100 dark:hover:bg-white/5 rounded transition"
            aria-label="Close"
          ><X size={16}/></button>
        </div>

        <div className="p-5 space-y-5">
          {busy && (
            <div className="text-sm text-slate-500 flex items-center gap-2">
              <Loader2 size={14} className="animate-spin"/> Loading details…
            </div>
          )}
          {cr.title && (
            <>
              {/* Badges */}
              <div className="flex flex-wrap items-center gap-1.5">
                {cr.severity && <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-[0.18em] ${SEV[cr.severity] || SEV.P2}`}>{cr.severity}</span>}
                {cr.classification && <span className="text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-[0.18em] bg-slate-100 text-slate-700 dark:bg-white/10 dark:text-white/70">{cr.classification}</span>}
              </div>

              {/* Title + summary */}
              <div>
                <h2 className="display text-2xl font-bold leading-tight">{cr.title}</h2>
                <p className="mt-2 text-sm text-slate-600 dark:text-white/75 leading-relaxed">{cr.summary}</p>
              </div>

              {/* Acceptance criteria */}
              {cr.acceptance_criteria?.length > 0 && (
                <div className="ve-card p-4">
                  <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5]">Acceptance criteria</div>
                  <ul className="mt-2 space-y-1">
                    {cr.acceptance_criteria.map((a, i) => (
                      <li key={i} className="text-sm text-slate-700 dark:text-white/80 leading-snug">
                        <span className="text-[#5E1F8B] dark:text-[#8B3FC5] font-bold mr-1">›</span> {a}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Iqra's reading */}
              {cr.iqra_thoughts && (
                <div className="ve-card p-4 border-l-2 border-l-[#8B3FC5]">
                  <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5]">Iqra&rsquo;s reading</div>
                  <p className="text-sm italic text-slate-600 dark:text-white/75 mt-1.5 leading-relaxed">{cr.iqra_thoughts}</p>
                </div>
              )}

              {/* User's raw text */}
              <div className="ve-card p-4">
                <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5]">User&rsquo;s exact words</div>
                <p className="text-sm text-slate-600 dark:text-white/75 mt-1.5 whitespace-pre-wrap leading-relaxed">{cr.user_text}</p>
                <div className="mt-3 flex flex-wrap gap-3 text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-white/55">
                  <span>{cr.submitted_by_email}</span>
                  <span>{cr.submitted_by_role}</span>
                  {cr.page_url && <span className="mono normal-case tracking-normal text-slate-400">{cr.page_url}</span>}
                  <span>{new Date(cr.created_at).toLocaleString()}</span>
                </div>
              </div>

              {/* Screenshot */}
              {cr.screenshot_data_url && (
                <div>
                  <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5] mb-2 inline-flex items-center gap-1.5">
                    <ImageIcon size={12}/> Screenshot
                  </div>
                  <div className="rounded-lg border border-slate-200 dark:border-white/10 overflow-hidden">
                    <img src={cr.screenshot_data_url} alt="Screenshot at submission" className="w-full object-contain max-h-[600px]"/>
                  </div>
                </div>
              )}

              {/* Status workflow */}
              <div className="ve-card p-4">
                <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5]">Move to</div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {STATUS_OPTIONS.map((s) => {
                    const active = cr.status === s.id;
                    return (
                      <button
                        key={s.id}
                        data-testid={`cr-set-${s.id}`}
                        onClick={() => onUpdateStatus(s.id)}
                        disabled={active}
                        className={`text-[10px] font-bold px-2.5 py-1.5 rounded uppercase tracking-[0.18em] transition
                          ${active
                            ? `${s.tone} cursor-default ring-2 ring-[#8B3FC5]/30`
                            : 'border border-slate-200 dark:border-white/10 hover:border-[#8B3FC5] text-slate-700 dark:text-white/75'}`}
                      >
                        {s.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Admin notes */}
              <div className="ve-card p-4">
                <div className="ve-eyebrow text-[#5E1F8B] dark:text-[#8B3FC5]">Admin notes</div>
                <textarea
                  data-testid="cr-admin-notes"
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. Bundled with Q2 UX sweep · assigned to dev team."
                  className="mt-2 w-full px-3 py-2 rounded-md border border-slate-200 dark:border-white/10
                             bg-white dark:bg-white/5 text-sm focus:outline-none focus:border-[#8B3FC5]
                             focus:ring-2 focus:ring-[#8B3FC5]/20 resize-none"
                />
                <button
                  data-testid="cr-save-notes"
                  onClick={() => onSaveNotes(notes)}
                  className="mt-2 ve-btn-primary inline-flex items-center gap-2 text-[11px]"
                >
                  Save notes <ArrowRight size={12}/>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
